import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useParams } from "react-router-dom";

import { api, getPosHeaders } from "./api";
import { usePermissions } from "./PermissionContext";

import POSElectronicInvoice from "./POSElectronicInvoice";
import POSInvoiceSettings from "./POSInvoiceSettings";


const STATUS_LABELS = {
  not_prepared: "Sin borrador",
  draft: "Borrador",
  pending: "Pendiente",
  processing: "En proceso",
  validated: "Validada",
  rejected: "Rechazada",
};

const PAGE_SIZE = 20;

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

function errorText(error) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return error.message || "No fue posible consultar las ventas.";
}


export default function POSElectronicInvoices() {
  const { company } = useParams();
  const { can, loadingPermissions } = usePermissions();

  const canView = can("billing.view");
  const canManage = can("billing.manage");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);

  const [result, setResult] = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);

  const requestVersionRef = useRef(0);
  const detailRef = useRef(null);

  useEffect(() => {
    setSelectedSale(null);
    setResult(null);
    setPage(1);
  }, [company]);

  useEffect(() => {
    const controller = new AbortController();
    const version = ++requestVersionRef.current;

    if (loadingPermissions || !canView) {
      setLoading(loadingPermissions);
      setResult(null);
      return () => controller.abort();
    }

    setLoading(true);
    setError("");

    const timer = window.setTimeout(async () => {
      try {
        const { data } = await api.get(
          "/pos/electronic-invoice-dashboard/me",
          {
            headers: getPosHeaders(company),
            signal: controller.signal,
            params: {
              search: search.trim() || undefined,
              document_status: status,
              limit: PAGE_SIZE,
              offset: (page - 1) * PAGE_SIZE,
            },
          }
        );

        if (
          controller.signal.aborted ||
          version !== requestVersionRef.current
        ) {
          return;
        }

        if (!Array.isArray(data.items)) {
          throw new Error("La respuesta del listado es inválida.");
        }

        const lastPage = Math.max(
          1,
          Math.ceil(data.total / PAGE_SIZE)
        );

        if (page > lastPage) {
          setPage(lastPage);
          return;
        }

        setResult({ company, data });
      } catch (requestError) {
        if (
          !controller.signal.aborted &&
          version === requestVersionRef.current
        ) {
          setResult(null);
          setError(errorText(requestError));
        }
      } finally {
        if (
          !controller.signal.aborted &&
          version === requestVersionRef.current
        ) {
          setLoading(false);
        }
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [
    company,
    canView,
    loadingPermissions,
    search,
    status,
    page,
    reload,
  ]);

  useEffect(() => {
    if (selectedSale) {
      detailRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [selectedSale]);

  const refreshList = useCallback(() => {
    setReload((current) => current + 1);
  }, []);

  if (loadingPermissions) {
    return <p>Validando permisos…</p>;
  }

  if (!canView) {
    return (
      <div className="alert alert-warning" role="alert">
        No tienes permiso para consultar facturación electrónica.
      </div>
    );
  }

  const data =
    result?.company === company ? result.data : null;

  const items = data?.items || [];
  const summary = data?.summary || {};
  const totalPages = Math.max(
    1,
    Math.ceil((data?.total || 0) / PAGE_SIZE)
  );

  const currentSale =
    selectedSale?.company === company
      ? selectedSale
      : null;

  const cards = [
    ["Ventas", summary.total_sales],
    ["Sin borrador", summary.not_prepared],
    ["Borradores", summary.draft],
    ["Validadas", summary.validated],
    ["Con alertas", summary.alerts],
  ];

  return (
    <div className="container-fluid px-0">
      <header className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
        <div>
          <h1 className="h3">Facturación electrónica de ventas</h1>

          <p className="text-muted mb-0">
            Consulta las ventas, prepara sus borradores y revisa
            las alertas de facturación.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={refreshList}
          disabled={loading}
        >
          {loading ? "Consultando…" : "Actualizar listado"}
        </button>
      </header>

      <div className="row g-3 mb-4">
        {cards.map(([label, value]) => (
          <div className="col-6 col-lg" key={label}>
            <article className="bg-white border rounded p-3 h-100">
              <small className="text-muted d-block">
                {label}
              </small>

              <strong className="fs-4">
                {loading ? "…" : value ?? 0}
              </strong>
            </article>
          </div>
        ))}
      </div>

      <details className="mb-4">
        <summary className="btn btn-outline-secondary">
          Configuración y alertas generales
        </summary>

        <POSInvoiceSettings />
      </details>

      <section className="bg-white border rounded p-3 p-lg-4">
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-8">
            <label htmlFor="electronic-search" className="form-label">
              Buscar venta, cliente o factura
            </label>

            <input
              id="electronic-search"
              className="form-control"
              value={search}
              maxLength={100}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Número o nombre del cliente"
            />
          </div>

          <div className="col-12 col-md-4">
            <label htmlFor="electronic-status" className="form-label">
              Estado electrónico
            </label>

            <select
              id="electronic-status"
              className="form-select"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="all">Todos</option>

              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <p role="status">Consultando ventas…</p>
        ) : !error && (
          <>
            <p className="small text-muted">
              {data?.total || 0} resultado(s).
              Los contadores superiores corresponden a la búsqueda
              en todos los estados.
            </p>

            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>Venta</th>
                    <th>Cliente</th>
                    <th>Total</th>
                    <th>Estado electrónico</th>
                    <th>Documento</th>
                    <th>Alertas</th>
                    <th>Acción</th>
                  </tr>
                </thead>

                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4">
                        No hay ventas para estos filtros.
                      </td>
                    </tr>
                  ) : items.map((sale) => {
                    const document = sale.document;
                    const documentStatus =
                      document?.status || "not_prepared";

                    return (
                      <tr key={sale.sale_id}>
                        <td>
                          <strong>{sale.sale_reference}</strong>

                          <small className="d-block text-muted">
                            {sale.sale_date
                              ? new Date(sale.sale_date).toLocaleString(
                                  "es-CO",
                                  { timeZone: "America/Bogota" }
                                )
                              : "Sin fecha"}
                          </small>
                        </td>

                        <td>{sale.customer_name}</td>
                        <td>{money(sale.total)}</td>

                        <td>
                          <span className={`badge ${
                            documentStatus === "validated"
                              ? "bg-success"
                              : documentStatus === "rejected"
                                ? "bg-danger"
                                : "bg-secondary"
                          }`}>
                            {STATUS_LABELS[documentStatus] ||
                              documentStatus}
                          </span>
                        </td>

                        <td>
                          {document?.number
                            ? `${document.prefix || ""}${document.number}`
                            : document
                              ? `Borrador #${document.id}`
                              : "Sin preparar"}
                        </td>

                        <td style={{ maxWidth: 280 }}>
                          {sale.sale_status === "cancelled" && (
                            <small className="d-block text-danger">
                              Venta anulada.
                            </small>
                          )}

                          {document?.last_error ? (
                            <small className="d-block text-danger text-break">
                              {document.last_error}
                            </small>
                          ) : documentStatus === "rejected" ? (
                            <small className="text-danger">
                              Documento rechazado.
                            </small>
                          ) : sale.sale_status !== "cancelled" ? (
                            <span className="text-muted">—</span>
                          ) : null}
                        </td>

                        <td>
                          {document || (sale.can_prepare && canManage) ? (
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => setSelectedSale({
                                company,
                                id: sale.sale_id,
                                reference: sale.sale_reference,
                              })}
                            >
                              {document ? "Ver documento" : "Preparar borrador"}
                            </button>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-between align-items-center gap-3 mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((current) => current - 1)}
              >
                Anterior
              </button>

              <span className="small">
                Página {page} de {totalPages}
              </span>

              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={page >= totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                Siguiente
              </button>
            </div>
          </>
        )}
      </section>

      {currentSale && (
        <section ref={detailRef} className="mt-4">
          <div className="d-flex justify-content-between align-items-center gap-3 mb-2">
            <h2 className="h5 mb-0">
              Documento de la venta {currentSale.reference}
            </h2>

            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                setSelectedSale(null);
                refreshList();
              }}
            >
              Cerrar detalle y actualizar listado
            </button>
          </div>

          <POSElectronicInvoice
            key={`${company}:${currentSale.id}`}
            saleId={currentSale.id}
            saleReference={currentSale.reference}
          />
        </section>
      )}
    </div>
  );
}